import React, { useState, useContext } from 'react';
import { Flag, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from './Toast';

const ReportButton = ({ targetType, targetId, targetTitle, className = '' }) => {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ reason: 'contenu_illicite', description: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/reports', {
        target_type: targetType,
        target_id: targetId,
        reason: form.reason,
        description: form.description
      });
      setSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
        setForm({ reason: 'contenu_illicite', description: '' });
      }, 2000);
    } catch (err) {
      if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Erreur lors du signalement');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null; // Pas connecté, pas de bouton
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-lg border border-red-200 transition font-medium ${className}`}
        title="Signaler ce contenu"
      >
        <Flag className="w-4 h-4" />
        <span className="text-sm">Signaler</span>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            {success ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800">Signalement envoyé</h3>
                <p className="text-gray-600 mt-2">Merci pour votre vigilance. Notre équipe examinera ce signalement.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2"><Flag className="w-5 h-5 text-red-500" /> Signaler un contenu</h2>
                  <button 
                    onClick={() => setShowModal(false)} 
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>{targetType === 'project' ? 'Projet' : 'Tâche'}:</strong> {targetTitle || `#${targetId}`}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Raison du signalement</label>
                    <select
                      value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                    >
                      <option value="contenu_illicite">⊘ Contenu illicite</option>
                      <option value="spam">Spam / Publicité abusive</option>
                      <option value="harcelement">Harcèlement / Propos haineux</option>
                      <option value="fraude">Fraude / Arnaque</option>
                      <option value="droits_auteur">©️ Violation des droits d'auteur</option>
                      <option value="autre">Autre raison</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                      placeholder="Décrivez plus en détail le problème..."
                    />
                  </div>

                  <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" /> Les faux signalements peuvent entraîner des sanctions sur votre compte.
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? 'Envoi...' : <><Flag className="w-4 h-4" /> Envoyer le signalement</>}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportButton;
